import { useState } from 'react'
import { useLocation, useNavigate } from 'react-router'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { AlertCircle, ShieldCheck } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { usePlatformAuthStore } from '../store'
import { toApiProblem } from '@/lib/api/client'
import { problemMessage } from '@/lib/api/problem'

const schema = z.object({
  email: z.string().min(1, 'Email is required').email('Enter a valid email address'),
  password: z.string().min(1, 'Password is required'),
})

type Values = z.infer<typeof schema>

/**
 * Operator sign-in.
 *
 * <p>Dark, and nothing like the customer's login page. Somebody who reaches this by
 * accident should be able to tell at a glance that it is not the product, and somebody
 * who reaches the product's login expecting this should notice too.
 */
export function PlatformLoginPage() {
  const navigate = useNavigate()
  const location = useLocation()
  const login = usePlatformAuthStore((state) => state.login)
  const [serverError, setServerError] = useState<string | null>(null)

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<Values>({ resolver: zodResolver(schema) })

  async function onSubmit(values: Values) {
    setServerError(null)
    try {
      await login(values.email, values.password)
      const from = (location.state as { from?: string } | null)?.from ?? '/platform/tenants'
      void navigate(from, { replace: true })
    } catch (error) {
      setServerError(problemMessage(toApiProblem(error) ?? error, 'Invalid email or password'))
    }
  }

  return (
    <div className="grid min-h-dvh place-items-center bg-slate-900 bg-[radial-gradient(ellipse_at_top,rgb(108_92_231/0.35),transparent_60%)] p-4">
      <div className="w-full max-w-sm rounded-2xl border border-white/10 bg-[var(--color-card)] p-8 shadow-2xl">
        <div className="mb-6 flex items-center gap-3 text-[var(--color-heading)]">
          <span className="grid size-11 place-items-center rounded-xl bg-[var(--color-primary-soft)] text-[var(--color-primary)]"><ShieldCheck className="size-6" aria-hidden /></span>
          <div>
            <h1 className="text-lg font-semibold">Operations</h1>
            <p className="text-sm text-[var(--color-muted)]">HUMIS installation console</p>
          </div>
        </div>

        {serverError && (
          <div
            role="alert"
            className="mb-4 flex items-start gap-2 rounded-[var(--radius-control)] bg-[var(--color-tint-red)] p-3 text-sm text-[var(--color-danger)]"
          >
            <AlertCircle className="mt-0.5 size-4 shrink-0" aria-hidden />
            <span>{serverError}</span>
          </div>
        )}

        <form onSubmit={(event) => void handleSubmit(onSubmit)(event)} className="space-y-4" noValidate>
          <div className="space-y-1.5">
            <Label htmlFor="operator-email">Email</Label>
            <Input id="operator-email" type="email" autoComplete="username" {...register('email')} />
            {errors.email && <p className="text-xs text-[var(--color-danger)]">{errors.email.message}</p>}
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="operator-password">Password</Label>
            <Input
              id="operator-password"
              type="password"
              autoComplete="current-password"
              {...register('password')}
            />
            {errors.password && (
              <p className="text-xs text-[var(--color-danger)]">{errors.password.message}</p>
            )}
          </div>

          <Button type="submit" className="w-full" disabled={isSubmitting}>
            {isSubmitting ? 'Signing in…' : 'Sign in'}
          </Button>
        </form>

        <p className="mt-6 text-xs text-[var(--color-muted)]">
          This console manages every workspace on this installation. It is not a customer
          sign-in.
        </p>
      </div>
    </div>
  )
}
